import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { roomTypesApi } from '../services/api';

interface RoomType {
  id: number;
  name: string;
  description: string | null;
  base_rate: number;
  max_occupancy: number;
  bed_type: string | null;
  amenities: string | null;
  created_at: string;
}

const RoomTypes: React.FC = () => {
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState<RoomType | null>(null);

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const fetchRoomTypes = async () => {
    try {
      setLoading(true);
      const response = await roomTypesApi.getAll();
      setRoomTypes(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load room types');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (roomType: RoomType) => {
    setEditingRoomType(roomType);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRoomType(null);
  };

  const handleSave = async () => {
    try {
      const formData = new FormData(document.getElementById('room-type-form') as HTMLFormElement);
      const roomTypeData = {
        name: formData.get('name'),
        description: formData.get('description') || null,
        base_rate: parseFloat(formData.get('base_rate') as string),
        max_occupancy: parseInt(formData.get('max_occupancy') as string),
        bed_type: formData.get('bed_type') || null,
        amenities: formData.get('amenities') || null,
      };

      if (editingRoomType) {
        await roomTypesApi.update(editingRoomType.id, roomTypeData);
      } else {
        await roomTypesApi.create(roomTypeData);
      }
      handleCloseDialog();
      fetchRoomTypes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save room type');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const stats = {
    total: roomTypes.length,
    totalRevenue: roomTypes.reduce((sum, rt) => sum + rt.base_rate, 0),
    avgRate: roomTypes.length > 0 ? roomTypes.reduce((sum, rt) => sum + rt.base_rate, 0) / roomTypes.length : 0,
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Room Types</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Room Type
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Room Types</Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Average Rate</Typography>
              <Typography variant="h4" color="primary.main">
                ${stats.avgRate.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Revenue Potential</Typography>
              <Typography variant="h4" color="success.main">
                ${stats.totalRevenue.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Base Rate</TableCell>
              <TableCell>Max Occupancy</TableCell>
              <TableCell>Bed Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roomTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No room types found. Add your first room type to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              roomTypes.map((roomType) => (
                <TableRow key={roomType.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {roomType.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {roomType.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      ${roomType.base_rate.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>{roomType.max_occupancy}</TableCell>
                  <TableCell>{roomType.bed_type || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(roomType)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this room type?')) {
                          try {
                            await roomTypesApi.delete(roomType.id);
                            fetchRoomTypes();
                          } catch (err: any) {
                            setError(err.response?.data?.message || 'Failed to delete room type');
                          }
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRoomType ? 'Edit Room Type' : 'Add New Room Type'}</DialogTitle>
        <form id="room-type-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent>
            <TextField
              fullWidth
              name="name"
              label="Room Type Name"
              margin="normal"
              defaultValue={editingRoomType?.name || ''}
              required
              helperText="e.g., Standard, Deluxe, Suite"
            />
            <TextField
              fullWidth
              name="description"
              label="Description"
              margin="normal"
              multiline
              rows={3}
              defaultValue={editingRoomType?.description || ''}
              helperText="Brief description of the room type"
            />
            <TextField
              fullWidth
              name="base_rate"
              label="Base Rate (per night)"
              type="number"
              margin="normal"
              defaultValue={editingRoomType?.base_rate || ''}
              required
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            />
            <TextField
              fullWidth
              name="max_occupancy"
              label="Max Occupancy"
              type="number"
              margin="normal"
              defaultValue={editingRoomType?.max_occupancy || ''}
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              name="bed_type"
              label="Bed Type"
              margin="normal"
              defaultValue={editingRoomType?.bed_type || ''}
              helperText="e.g., King, Queen, Twin"
            />
            <TextField
              fullWidth
              name="amenities"
              label="Amenities"
              margin="normal"
              multiline
              rows={2}
              defaultValue={editingRoomType?.amenities || ''}
              helperText="Comma-separated list of amenities"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingRoomType ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default RoomTypes;

